// alertsController.js - Admin CRM Alerts (#2314): Cloud Monitoring's alerts, read through listMonitoringAlerts from its
// Alerts API. What is open now, how many fired, where they fired most this week, and the history by day, each with its
// links to the Cloud Console and the function's logs. Every alert also emails alerts@. An admin marks each alert New,
// Looking into it or Handled, with a note (#2374); setAlertNote keeps those, since the Alerts API is read-only.
import { BADGE, BADGE_DOT, BUTTON, EMPTY_STATE, PILL, checkbox } from "./ui/classes.js";
import { icon, solidIcon } from "./ui/icons.js";
import { setNavBadge } from "./ui/shell.js";
import { withBusy } from "./ui/busy.js";
import { hideDialog, showDialog } from "./ui/dialog.js";

const PROJECT_ID = "weeknightbite-83965";
const ISSUES_URL = "https://github.com/anton2lukas1/weeknightbite/issues/";
const REFRESH_MS = 2 * 60 * 1000;
/** A read younger than this is drawn again on opening the page instead of asking Google again. */
const FRESH_MS = 60 * 1000;
const DAY_MS = 24 * 60 * 60 * 1000;
const NOISY_LIMIT = 8;
/** The most other alerts setAlertNote marks in one save. */
const MAX_ALSO = 200;

const SEVERITIES = {
  CRITICAL: { label: "Critical", badge: BADGE.red },
  ERROR: { label: "Error", badge: BADGE.red },
  WARNING: { label: "Warning", badge: BADGE.yellow }
};

/** What an admin marked an alert (#2374). Every alert nobody has marked is New. */
const STATUSES = {
  new: { label: "New", badge: BADGE.paprika },
  investigating: { label: "Looking into it", badge: BADGE.blue },
  handled: { label: "Handled", badge: BADGE.green }
};
const STATUS_BUTTON = "rounded-md hover:opacity-80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-paprika-600 dark:focus-visible:outline-paprika-500";

let activeDeps = null;
/** The last listMonitoringAlerts answer, with `received_at`; its error when the last read failed. */
let result = null;
let readError = null;
let pending = null;
let filters = { policy: "all", term: "", hideHandled: false };
/** The alert the note dialog is open on, and the ids of the others its save may mark too. */
let editing = null;
let editingAlso = [];

function escapeHTML(str) {
  return String(str ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

/** What an alert fired on: the function, the scheduled job's function, the uptime check's host, else the resource type. */
export function alertTarget(a) {
  const labels = a?.resource?.labels || {};
  if (labels.service_name) return labels.service_name;
  if (labels.job_id) return String(labels.job_id).replace(/^firebase-schedule-/, "").replace(/-[a-z]+-[a-z]+\d+$/, "");
  return labels.host || a?.metric?.labels?.check_id || a?.resource?.type || "—";
}

/** "45 s", "3 min 26 s", "2 h 10 min", "1 d 4 h". */
export function durationText(ms) {
  if (!Number.isFinite(ms) || ms < 0) return "";
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s} s`;
  const m = Math.floor(s / 60);
  if (m < 60) return s % 60 ? `${m} min ${s % 60} s` : `${m} min`;
  const h = Math.floor(m / 60);
  if (h < 24) return m % 60 ? `${h} h ${m % 60} min` : `${h} h`;
  const d = Math.floor(h / 24);
  return h % 24 ? `${d} d ${h % 24} h` : `${d} d`;
}

/** How long it lasted, or has been open so far. */
export function alertDuration(a, now = new Date()) {
  const start = Date.parse(a?.open_time);
  const end = a?.close_time ? Date.parse(a.close_time) : now.getTime();
  return durationText(end - start);
}

/** The alert's status: what an admin marked, else New. */
export function alertStatus(a) {
  return STATUSES[a?.note?.status] ? a.note.status : "new";
}

/** True while nobody has marked the alert: no note, or a New one with no text (setAlertNote's rule). */
export function isUnmarked(a) {
  return !a?.note || (alertStatus(a) === "new" && !a.note.text);
}

/** The same policy's other alerts on the same function that nobody has marked: what one save can mark too. */
export function sameSourceUnmarked(alerts, a) {
  return alerts
    .filter((x) => x.id && x.id !== a.id && x.policy?.name === a.policy?.name && alertTarget(x) === alertTarget(a) && isUnmarked(x))
    .slice(0, MAX_ALSO);
}

function median(values) {
  if (!values.length) return null;
  const sorted = [...values].sort((x, y) => x - y);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

/** Open now, still New, fired in the last 24 hours and 7 days, and the median time to resolve of the week's closed ones. */
export function alertStats(alerts, now = new Date()) {
  const t = now.getTime();
  const age = (a) => t - Date.parse(a.open_time);
  const week = alerts.filter((a) => age(a) <= 7 * DAY_MS);
  return {
    open: alerts.filter((a) => a.state === "open").length,
    newAlerts: alerts.filter((a) => alertStatus(a) === "new").length,
    day: alerts.filter((a) => age(a) <= DAY_MS).length,
    week: week.length,
    medianMs: median(week.filter((a) => a.state === "closed" && a.close_time).map((a) => Date.parse(a.close_time) - Date.parse(a.open_time)))
  };
}

export function statsHTML(stats) {
  const card = (label, value, alarm = false) => `
    <div class="overflow-hidden rounded-lg bg-white px-4 py-5 shadow-xs outline-1 outline-black/5 sm:p-6 dark:bg-gray-800/50 dark:shadow-none dark:-outline-offset-1 dark:outline-white/10">
      <dt class="text-sm font-medium text-gray-500 dark:text-gray-400">${label}</dt>
      <dd class="${alarm ? "mt-1 text-3xl font-semibold tracking-tight text-red-600 dark:text-red-400" : "mt-1 text-3xl font-semibold tracking-tight text-gray-900 dark:text-white"}">${escapeHTML(value)}</dd>
    </div>`;
  return [
    card("Open now", stats.open, stats.open > 0),
    card("To look at", stats.newAlerts, stats.newAlerts > 0),
    card("Fired in the last 24 hours", stats.day),
    card("Fired in the last 7 days", stats.week),
    card("Median time to resolve, 7 days", stats.medianMs === null ? "—" : durationText(stats.medianMs))
  ].join("");
}

function severityBadge(a) {
  const s = SEVERITIES[a?.policy?.severity];
  return s ? `<span class="${s.badge}">${s.label}</span>` : `<span class="${BADGE.gray}">${escapeHTML(a?.policy?.severity ? String(a.policy.severity).toLowerCase() : "no severity")}</span>`;
}

/** The alert's status, as the button that opens its status and note. */
export function statusButtonHTML(a) {
  const s = STATUSES[alertStatus(a)];
  if (!a?.id) return `<span class="${s.badge}">${s.label}</span>`;
  const name = escapeHTML(`${alertTarget(a)}, ${a.policy?.name || ""}`);
  return `<button type="button" data-alert-note="${escapeHTML(a.id)}" class="${STATUS_BUTTON}"><span class="${s.badge}">${s.label}${solidIcon("pencil-square", "size-3 opacity-60")}</span><span class="sr-only">: change the status and note of ${name}</span></button>`;
}

/** A note's text, escaped, with "#123" linked to that issue. */
export function noteTextHTML(text) {
  // Not after "&" or a letter: escaping writes an apostrophe as "&#039;".
  return escapeHTML(text).replace(/(^|[^&\w])#(\d{1,6})\b/g, (_, before, n) =>
    `${before}<a href="${ISSUES_URL}${n}" target="_blank" rel="noopener noreferrer" class="${BUTTON.link}">#${n}</a>`);
}

/** "just now", "5 min ago", "3 h ago", else the date. */
function agoText(iso, now = new Date()) {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return "";
  return now.getTime() - t < DAY_MS ? relativeTime(t, now.getTime()) : new Date(t).toLocaleDateString(undefined, { day: "numeric", month: "short" });
}

/** The note under an alert: its text, then who marked it and when. Empty for an alert nobody has marked. */
export function noteLineHTML(a, now = new Date(), { truncate = false } = {}) {
  const note = a?.note;
  if (!note) return "";
  const who = [String(note.updated_by || "").split("@")[0], agoText(note.updated_at, now)].filter(Boolean).join(", ");
  const by = who ? `<span class="text-gray-400 dark:text-gray-500">${note.text ? " · " : ""}${escapeHTML(who)}</span>` : "";
  const title = truncate && note.text ? ` title="${escapeHTML(note.text)}"` : "";
  return `<p class="${truncate ? "mt-0.5 truncate text-xs text-gray-700 dark:text-gray-300" : "mt-1 text-xs text-gray-700 dark:text-gray-300"}"${title}>${note.text ? noteTextHTML(note.text) : ""}${by}</p>`;
}

/**
 * The function's (or the scheduled job's) logs in Logs Explorer, from ten minutes before the alert opened to ten after
 * it closed, or until now. Null where there are no logs to point at (an uptime check).
 */
export function logsUrl(a, now = new Date()) {
  const labels = a?.resource?.labels || {};
  let query = null;
  if (a?.resource?.type === "cloud_run_revision" && labels.service_name) {
    query = `resource.type="cloud_run_revision"\nresource.labels.service_name="${labels.service_name}"\nseverity>=WARNING`;
  } else if (a?.resource?.type === "cloud_scheduler_job" && labels.job_id) {
    query = `resource.type="cloud_scheduler_job"\nresource.labels.job_id="${labels.job_id}"`;
  }
  const start = Date.parse(a?.open_time);
  if (!query || Number.isNaN(start)) return null;
  const end = a.close_time ? Date.parse(a.close_time) : now.getTime();
  const range = `${new Date(start - 10 * 60000).toISOString()}%2F${new Date(end + 10 * 60000).toISOString()}`;
  return `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(query)};timeRange=${range}?project=${PROJECT_ID}`;
}

function linksHTML(a, now, { size = "small" } = {}) {
  const cls = size === "small" ? BUTTON.small : BUTTON.secondary;
  const logs = logsUrl(a, now);
  const name = escapeHTML(`${a.policy?.name || ""}, ${alertTarget(a)}`);
  return [
    logs ? `<a href="${escapeHTML(logs)}" target="_blank" rel="noopener noreferrer" class="${cls}">Logs<span class="sr-only">, ${name}</span></a>` : "",
    a.console_url ? `<a href="${escapeHTML(a.console_url)}" target="_blank" rel="noopener noreferrer" class="${cls}">Incident<span class="sr-only">, ${name}</span></a>` : ""
  ].filter(Boolean).join("");
}

function fullTime(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleString(undefined, { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function clockTime(iso) {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : d.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });
}

/** The alerts open now, oldest first: the one open longest is the first to look at. */
export function openAlertsHTML(alerts, now = new Date()) {
  const open = alerts.filter((a) => a.state === "open").sort((x, y) => String(x.open_time).localeCompare(String(y.open_time)));
  if (!open.length) {
    return `<li class="flex items-center gap-x-3 py-5">${icon("check-circle", "size-6 flex-none text-green-600 dark:text-green-400")}<p class="text-sm text-gray-700 dark:text-gray-300">Nothing is open. Every policy is quiet.</p></li>`;
  }
  return open.map((a) => `
    <li data-alert-id="${escapeHTML(a.id)}" class="flex flex-wrap items-center justify-between gap-x-6 gap-y-3 py-4">
      <div class="min-w-0">
        <div class="flex flex-wrap items-center gap-x-3 gap-y-1">
          <span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Open</span>
          <p class="text-sm/6 font-semibold text-gray-900 dark:text-white">${escapeHTML(a.policy?.name)}</p>
          ${severityBadge(a)}
          ${statusButtonHTML(a)}
        </div>
        <div class="mt-1 flex flex-wrap items-center gap-x-2 text-xs/5 text-gray-500 dark:text-gray-400">
          <p class="font-mono text-gray-700 dark:text-gray-300">${escapeHTML(alertTarget(a))}</p>
          <svg viewBox="0 0 2 2" class="size-0.5 fill-current" aria-hidden="true"><circle r="1" cx="1" cy="1" /></svg>
          <p class="whitespace-nowrap">open for ${escapeHTML(alertDuration(a, now))}</p>
          <svg viewBox="0 0 2 2" class="size-0.5 fill-current" aria-hidden="true"><circle r="1" cx="1" cy="1" /></svg>
          <p class="whitespace-nowrap">since <time datetime="${escapeHTML(a.open_time)}">${escapeHTML(fullTime(a.open_time))}</time></p>
        </div>
        ${noteLineHTML(a, now)}
      </div>
      <div class="flex flex-none items-center gap-x-2">${linksHTML(a, now, { size: "large" })}</div>
    </li>`).join("");
}

/** The last 7 days' alerts counted by policy and where they fired, most first. */
export function noisyAlerts(alerts, now = new Date()) {
  const counts = new Map();
  alerts.filter((a) => now.getTime() - Date.parse(a.open_time) <= 7 * DAY_MS).forEach((a) => {
    const key = `${a.policy?.name}\u0000${alertTarget(a)}`;
    const entry = counts.get(key) || { policy: a.policy?.name || "", target: alertTarget(a), count: 0 };
    entry.count += 1;
    counts.set(key, entry);
  });
  return [...counts.values()].sort((x, y) => y.count - x.count || x.target.localeCompare(y.target));
}

export function noisyHTML(alerts, now = new Date()) {
  const rows = noisyAlerts(alerts, now);
  if (!rows.length) return `<li class="text-sm text-gray-500 dark:text-gray-400">No alert in the last 7 days.</li>`;
  const max = rows[0].count;
  const shown = rows.slice(0, NOISY_LIMIT);
  const rest = rows.length - shown.length;
  return shown.map((r) => `
    <li data-noisy-target="${escapeHTML(r.target)}">
      <div class="flex items-baseline justify-between gap-3 text-sm">
        <span class="truncate font-mono text-gray-900 dark:text-white" title="${escapeHTML(r.policy)}">${escapeHTML(r.target)}</span>
        <span class="flex-none font-semibold text-gray-900 tabular-nums dark:text-white">${r.count}</span>
      </div>
      <p class="truncate text-xs text-gray-500 dark:text-gray-400">${escapeHTML(r.policy)}</p>
      <div class="mt-1 h-1.5 rounded-full bg-gray-100 dark:bg-white/10"><div class="h-1.5 rounded-full bg-paprika-500" style="width: ${Math.max(4, Math.round((r.count / max) * 100))}%"></div></div>
    </li>`).join("") + (rest > 0 ? `<li class="text-xs text-gray-500 dark:text-gray-400">and ${rest} more</li>` : "");
}

/** The alerts the history shows: one policy or all, without the handled ones if asked, then the search, notes included. */
export function visibleAlerts(alerts, { policy = "all", term = "", hideHandled = false } = {}) {
  const t = term.trim().toLowerCase();
  return alerts
    .filter((a) => policy === "all" || a.policy?.name === policy)
    .filter((a) => !hideHandled || alertStatus(a) !== "handled")
    .filter((a) => {
      if (!t) return true;
      const hay = [a.policy?.name, alertTarget(a), a.note?.text, ...Object.values(a.resource?.labels || {}), ...Object.values(a.metric?.labels || {})].join(" ").toLowerCase();
      return hay.includes(t);
    });
}

function dayKey(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

/** "Today", "Yesterday", else the weekday and date, in the admin's own time zone. */
export function dayLabel(iso, now = new Date()) {
  const d = new Date(iso);
  const yesterday = new Date(now.getTime() - DAY_MS);
  if (dayKey(d) === dayKey(now)) return "Today";
  if (dayKey(d) === dayKey(yesterday)) return "Yesterday";
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
}

/** The history, newest first, one group of rows per day. */
export function historyHTML(alerts, now = new Date()) {
  if (!alerts.length) return `<tr><td colspan="6" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">No alert matches.</td></tr>`;
  const groups = [];
  alerts.forEach((a) => {
    const key = dayKey(new Date(a.open_time));
    if (!groups.length || groups[groups.length - 1].key !== key) groups.push({ key, label: dayLabel(a.open_time, now), alerts: [] });
    groups[groups.length - 1].alerts.push(a);
  });
  return groups.map((g) => `
    <tr data-alerts-day="${escapeHTML(g.key)}" class="border-t border-gray-200 dark:border-white/10">
      <th scope="colgroup" colspan="6" class="bg-gray-50 py-2 pr-3 pl-4 text-left text-sm font-semibold text-gray-900 sm:pl-6 dark:bg-white/5 dark:text-white">${escapeHTML(g.label)} <span class="font-normal text-gray-500 dark:text-gray-400">· ${g.alerts.length} alert${g.alerts.length === 1 ? "" : "s"}</span></th>
    </tr>` + g.alerts.map((a, i) => `
    <tr data-alert-id="${escapeHTML(a.id)}" class="${i === 0 ? "border-t border-gray-300 dark:border-white/15" : "border-t border-gray-200 dark:border-white/10"}">
      <td class="py-3 pr-3 pl-4 text-sm whitespace-nowrap text-gray-500 tabular-nums sm:pl-6 dark:text-gray-400" title="${escapeHTML(fullTime(a.open_time))}">${escapeHTML(clockTime(a.open_time))}</td>
      <td class="max-w-0 min-w-48 px-3 py-3 text-sm">
        <p class="truncate font-medium text-gray-900 dark:text-white">${escapeHTML(alertTarget(a))}</p>
        <p class="truncate text-gray-500 dark:text-gray-400">${escapeHTML(a.policy?.name)}</p>
        ${noteLineHTML(a, now, { truncate: true })}
      </td>
      <td class="px-3 py-3 text-sm whitespace-nowrap">${severityBadge(a)}</td>
      <td class="px-3 py-3 text-sm whitespace-nowrap">${a.state === "open"
        ? `<span class="${BADGE.red}"><span aria-hidden="true" class="${BADGE_DOT.red}"></span>Open ${escapeHTML(alertDuration(a, now))}</span>`
        : `<span class="text-gray-700 tabular-nums dark:text-gray-300">${escapeHTML(alertDuration(a, now))}</span>`}</td>
      <td class="px-3 py-3 text-sm whitespace-nowrap">${statusButtonHTML(a)}</td>
      <td class="py-3 pr-4 pl-3 text-right text-sm whitespace-nowrap sm:pr-6"><span class="inline-flex gap-x-1.5">${linksHTML(a, now)}</span></td>
    </tr>`).join("")).join("");
}

/** The policy filter's options: every policy in the result, and the one picked even if it fired no more. */
export function policyOptionsHTML(alerts, selected = "all") {
  const names = [...new Set(alerts.map((a) => a.policy?.name).filter(Boolean))].sort();
  if (selected !== "all" && !names.includes(selected)) names.push(selected);
  return [`<option value="all">All policies</option>`, ...names.map((n) => `<option value="${escapeHTML(n)}"${n === selected ? " selected" : ""}>${escapeHTML(n)}</option>`)].join("");
}

function relativeTime(ms, now = Date.now()) {
  const min = Math.round((now - ms) / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min} min ago`;
  return `${Math.round(min / 60)} h ago`;
}

function setHTML(id, html) {
  const el = document.getElementById(id);
  if (el) el.innerHTML = html;
}

function setText(id, text) {
  const el = document.getElementById(id);
  if (el) el.textContent = text;
}

/** Draws the page from the last read, or says why there is none. The nav badge counts the alerts still New. */
function render() {
  const now = new Date();
  const alerts = result?.alerts || [];
  const updated = document.getElementById("alertsUpdated");
  if (updated) {
    updated.className = readError ? "mt-1 text-xs text-red-600 dark:text-red-400" : "mt-1 text-xs text-gray-500 dark:text-gray-400";
    updated.textContent = readError
      ? `Couldn't read the alerts: ${readError}${result ? ` Showing the read from ${relativeTime(result.received_at)}.` : ""}`
      : result ? `Read ${relativeTime(result.received_at)}, every 2 minutes while this page is open.${result.truncated ? " More alerts than one read takes: the oldest are left out." : ""}` : "Reading Cloud Monitoring…";
  }
  if (!result) {
    if (readError) {
      setHTML("alertsOpenList", `<li class="py-5"><div class="${EMPTY_STATE.box}"><p class="${EMPTY_STATE.title}">No alerts to show</p><p class="${EMPTY_STATE.text}">${escapeHTML(readError)}</p></div></li>`);
      setHTML("alertsHistoryBody", `<tr><td colspan="6" class="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">Nothing read yet.</td></tr>`);
    }
    return;
  }
  const stats = alertStats(alerts, now);
  setHTML("alertsStats", statsHTML(stats));
  setHTML("alertsOpenList", openAlertsHTML(alerts, now));
  setHTML("alertsNoisyList", noisyHTML(alerts, now));
  setHTML("alertsPolicyFilter", policyOptionsHTML(alerts, filters.policy));
  const windowEl = document.getElementById("alertsHistoryWindow");
  if (windowEl) windowEl.textContent = result.since ? `since ${new Date(result.since).toLocaleDateString(undefined, { day: "numeric", month: "short" })}` : "";
  setHTML("alertsHistoryBody", historyHTML(visibleAlerts(alerts, filters), now));
  setNavBadge("alerts", stats.newAlerts > 0 ? String(stats.newAlerts) : "", "danger");
}

/** Asks Google for the alerts. One read at a time: a second caller shares the one in flight. */
export function refreshAlerts({ quiet = true } = {}) {
  if (!activeDeps) return Promise.resolve();
  if (pending) return pending;
  const { functions, httpsCallable, showToast } = activeDeps;
  pending = (async () => {
    try {
      const { data } = await httpsCallable(functions, "listMonitoringAlerts")({});
      result = { ...data, alerts: Array.isArray(data?.alerts) ? data.alerts : [], received_at: Date.now() };
      readError = null;
    } catch (err) {
      readError = err?.message || String(err);
      console.warn("⚠️ listMonitoringAlerts failed:", err);
      if (!quiet) showToast?.(`Couldn't read the alerts: ${readError}`, "error");
    } finally {
      pending = null;
    }
    render();
  })();
  return pending;
}

/** The section opened: a read younger than a minute is drawn as it is, else Google is asked again. */
export function openAlerts() {
  if (result && !readError && Date.now() - result.received_at < FRESH_MS) {
    render();
    return Promise.resolve();
  }
  return refreshAlerts();
}

/** The dialog's chosen status, and whether its save would unmark the alert (New, no text). */
function dialogChoice() {
  const status = document.querySelector('input[name="alertNoteStatus"]:checked')?.value || "new";
  const text = (document.getElementById("alertNoteText")?.value || "").trim();
  return { status, text, unmark: status === "new" && !text };
}

/** "Also mark the others" shows while there are others and the save would keep a note. */
function syncAlsoRow() {
  const row = document.getElementById("alertNoteAlso");
  if (row) row.style.display = editingAlso.length && !dialogChoice().unmark ? "flex" : "none";
}

/** Opens one alert's status and note. */
export function openAlertNote(id) {
  const a = (result?.alerts || []).find((x) => x.id === id);
  if (!a) return false;
  const now = new Date();
  const also = sameSourceUnmarked(result.alerts, a);
  editing = a;
  editingAlso = also.map((x) => x.id);
  setText("alertNoteTitle", alertTarget(a));
  setText("alertNoteSummary", `${a.policy?.name || ""} · opened ${fullTime(a.open_time)} · ${a.state === "open" ? "open for" : "lasted"} ${alertDuration(a, now)}`);
  setHTML("alertNoteStatuses", Object.entries(STATUSES).map(([key, s]) =>
    `<label class="${PILL}"><input type="radio" name="alertNoteStatus" value="${key}" class="sr-only"${key === alertStatus(a) ? " checked" : ""}>${s.label}</label>`).join(""));
  const textEl = document.getElementById("alertNoteText");
  if (textEl) textEl.value = a.note?.text || "";
  // Newest first, so the last is the oldest.
  const since = also.length ? new Date(also[also.length - 1].open_time).toLocaleDateString(undefined, { day: "numeric", month: "short" }) : "";
  setHTML("alertNoteAlso", also.length ? `
    <div class="flex h-6 shrink-0 items-center">${checkbox('id="alertNoteAlsoCheck" aria-describedby="alertNoteAlsoHelp"')}</div>
    <div class="text-sm/6">
      <label for="alertNoteAlsoCheck" class="font-medium text-gray-900 dark:text-white">Also mark the ${also.length} other unmarked alert${also.length === 1 ? "" : "s"} from ${escapeHTML(alertTarget(a))}</label>
      <p id="alertNoteAlsoHelp" class="text-gray-500 dark:text-gray-400">${escapeHTML(a.policy?.name || "")}, since ${escapeHTML(since)}.</p>
    </div>` : "");
  syncAlsoRow();
  setText("alertNoteChanged", a.note?.updated_at ? `Last changed by ${a.note.updated_by || "an admin"}, ${fullTime(a.note.updated_at)}.` : "Nobody has marked it yet.");
  showDialog("alertNoteDialog");
  return true;
}

/** Saves the dialog's status and note, on the other unmarked alerts too when ticked, and draws what the server kept. */
export async function saveAlertNote() {
  if (!activeDeps || !editing) return;
  const { functions, httpsCallable, showToast } = activeDeps;
  const { status, text, unmark } = dialogChoice();
  const alsoIds = !unmark && document.getElementById("alertNoteAlsoCheck")?.checked ? editingAlso : [];
  try {
    const { data } = await httpsCallable(functions, "setAlertNote")({ alertId: editing.id, status, text, alsoIds });
    // A read that started before the save may carry the notes from before it: let it land first.
    if (pending) await pending;
    const notes = data?.notes || {};
    (result?.alerts || []).forEach((a) => {
      if (Object.prototype.hasOwnProperty.call(notes, a.id)) a.note = notes[a.id];
    });
    render();
    hideDialog("alertNoteDialog");
    editing = null;
    const marked = data?.also_marked || 0;
    const kept = alsoIds.length - marked;
    showToast?.(`${marked ? `Saved on ${marked + 1} alerts.` : "Saved."}${kept > 0 ? ` ${kept} kept the note someone gave ${kept === 1 ? "it" : "them"} meanwhile.` : ""}`, "success");
  } catch (err) {
    console.warn("⚠️ setAlertNote failed:", err);
    showToast?.(`Couldn't save the note: ${err?.message || err}`, "error");
  }
}

/**
 * Every 2 minutes while the Alerts page is on screen and the tab visible. Returns the stop function app.js runs at
 * sign-out (loadForAdmin).
 */
export function startAlertsAutoRefresh() {
  const handle = setInterval(() => {
    const section = document.getElementById("sectionAlerts");
    if (!section || section.style.display === "none" || document.visibilityState === "hidden") return;
    refreshAlerts();
  }, REFRESH_MS);
  return () => clearInterval(handle);
}

export async function loadAlertsTemplate() {
  const containerEl = document.getElementById("sectionAlertsContainer");
  if (!containerEl) return false;
  try {
    const res = await fetch("templates/alerts.html");
    if (!res.ok) throw new Error(`HTTP ${res.status} fetching alerts.html`);
    containerEl.innerHTML = await res.text();
    return true;
  } catch (err) {
    console.error("❌ Failed to load the alerts template:", err);
    return false;
  }
}

// Binds the controls and reads nothing: the template is mounted before anyone has signed in (#1333); app.js asks for
// the alerts once an admin is in.
export function initAlertsController(deps) {
  activeDeps = deps;
  document.getElementById("alertsRefreshBtn")?.addEventListener("click", (e) => {
    withBusy(e.currentTarget, () => refreshAlerts({ quiet: false }));
  });
  document.getElementById("alertsPolicyFilter")?.addEventListener("change", (e) => {
    filters = { ...filters, policy: e.target.value };
    render();
  });
  document.getElementById("alertsSearchInput")?.addEventListener("input", (e) => {
    filters = { ...filters, term: e.target.value };
    render();
  });
  document.getElementById("alertsHideHandled")?.addEventListener("change", (e) => {
    filters = { ...filters, hideHandled: e.target.checked };
    render();
  });
  // The status buttons in the open list and the history, which every read draws again.
  document.getElementById("sectionAlerts")?.addEventListener("click", (e) => {
    const button = e.target.closest?.("[data-alert-note]");
    if (button) openAlertNote(button.dataset.alertNote);
  });
  document.getElementById("alertNoteStatuses")?.addEventListener("change", syncAlsoRow);
  document.getElementById("alertNoteText")?.addEventListener("input", syncAlsoRow);
  document.getElementById("alertNoteCancelBtn")?.addEventListener("click", () => hideDialog("alertNoteDialog"));
  document.getElementById("alertNoteSaveBtn")?.addEventListener("click", (e) => {
    withBusy(e.currentTarget, saveAlertNote, { others: [document.getElementById("alertNoteCancelBtn")] });
  });
}
